import { Box, Flex } from "@chakra-ui/react";
import { Col, Row } from "antd";
import backgroundArrecadacoes from "assets/images/background-arrecadacoes.svg";
import useArrecadacoes from "features/arrecadacoes/useArrecadacoes";
import moment from "moment";
import { useEffect, useState, useMemo } from "react";
import ArrecadacoesTable from "../ArrecadacoesTable";
import OutlineButton from "components/Button/OutlineButton";
import SelectMonthYear from "components/Input/SelectMonthYear";
import PageHeader from "components/Page/PageHeader";
import useFiltroArrecadacao from "../../hooks/useFiltrosArrecadacao";
import DashboardArrecadacoes from "../DashboardArrecadacoes";
import ModalFormArrecadacao from "../../../CriarArrecadacao/components/ModalFormArrecadacao";
import ModalFormRateio from "components/Input/SelectRateio/ModalFormRateio";
import useFecharRateio from "features/rateio/hooks/useFecharRateio";
import { showConfirm } from "infra/helpers/alerta";
import { useWindowSize } from "infra/helpers/window";
import ModalParametrosCobranca from "components/Features/Parametros/ModalParametrosCobranca";
import ModalNotificacao from "components/Features/RoboCobranca/ModalNotificacao";
import { BiCog, BiBot } from "react-icons/bi";
import BoletosEmRegistro from "components/BoletosEmRegistro";
import useBoletosEmRegistro from "core/features/conta/hooks/useBoletosEmRegistro";
import { ArrecadacaoItemResponse } from "features/arrecadacoes/typings";
import ModalHistoricoArrecadacao from "./../ModalHistoricoArrecadacao";
import ModalParametrosArrecadacaoAutomatica from "../ModalParametrosArrecadacaoAutomatica/index";

function ListaArrecadacoes() {
  const [isOpenModalNotifyIsOpen, setIsOpenModalNotifyIsOpen] = useState(false);

  const [boletoExtra, setBoletoExtra] = useState<boolean | undefined>();
  const [isOpenModalParametros, setIsOpenModalParametros] =
    useState<boolean>(false);
  const [
    isOpenModalParametrosArrecadacaoAutomatica,
    setIsOpenModalParametrosArrecadacaoAutomatica,
  ] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<ArrecadacaoItemResponse[]>(
    []
  );
  const [idHistoricoArrecadacao, setIdHistoricoArrecadacao] = useState<
    string | undefined
  >();

  const [selectedDate, setSelectedDate] = useState(moment());
  const [isOpenModal, setIsOpenModal] = useState(false);

  const { filtro, onChangeFiltro } = useFiltroArrecadacao();
  const { data, isLoading, isFetching, refetch } = useArrecadacoes(filtro);

  const [arrecadacaoEditId, setArrecadacaoEditId] = useState<string>();
  const [rateio, setRateio] = useState<
    { id: string; nome: string } | undefined
  >();

  const { numeroBoletosEmRegistro, setNumeroBoletosEmRegistro } =
    useBoletosEmRegistro();

  const { mutate: fecharRateio, isLoading: isLoadingFecharRateio } =
    useFecharRateio({
      id: rateio?.id,
      year: moment(selectedDate).year().toString(),
      month: (moment(selectedDate).month() + 1).toString(),
      onSuccess: () => {
        refetch();
      },
    });

  useEffect(() => {
    onChangeFiltro({
      mes: selectedDate.format("MM"),
      ano: selectedDate.format("YYYY"),
    });
  }, [onChangeFiltro, selectedDate]);

  const items = useMemo(() => {
    if (!data) {
      return [];
    }

    if (boletoExtra === true) {
      return data.filter((item) => item.extra);
    }

    if (boletoExtra === false) {
      return data.filter((item) => !item.extra);
    }

    return data;
  }, [boletoExtra, data]);

  const _isLoading = isLoading || isFetching || isLoadingFecharRateio;

  const windowSize = useWindowSize();

  const isMobile = windowSize.width <= 580;

  return (
    <>
      <Flex>
        <Flex flex={1}>
          <PageHeader
            mb={!isMobile ? "" : "10rem"}
            mt={!isMobile ? "" : "4"}
            title="Arrecadações"
            titleButton="Nova arrecadação"
            midComponent={
              <SelectMonthYear
                mt={!isMobile ? "" : "4"}
                bg="white"
                value={selectedDate}
                onChange={(date) => date && setSelectedDate(date)}
              />
            }
            onClick={() => setIsOpenModal(true)}
          />
        </Flex>
        <Flex gap="8px" flexWrap="wrap" ml="12px" height="fit-content">
          <OutlineButton
            size="sm"
            onClick={() => setIsOpenModalNotifyIsOpen(true)}
            variant={"outline"}
          >
            <BiBot />
            Robô de Cobrança
          </OutlineButton>

          <OutlineButton
            size="sm"
            onClick={() => setIsOpenModalParametrosArrecadacaoAutomatica(true)}
            variant={"outline"}
          >
            <BiCog />
            Configurar arrecadações automáticas
          </OutlineButton>

          <OutlineButton
            size="sm"
            onClick={() => setIsOpenModalParametros(true)}
            variant={"outline"}
          >
            <BiCog />
            Parâmetros
          </OutlineButton>
        </Flex>
      </Flex>

      <Flex display={!isMobile ? "" : "none"}>
        <Row gutter={32} align="bottom">
          <Col sm={24} md={18}>
            <DashboardArrecadacoes />
          </Col>
          <Col sm={0} md={6}>
            <Flex
              mb={"-32px"}
              flex={1}
              w={"100%"}
              h={200}
              bgImage={backgroundArrecadacoes}
              bgRepeat="no-repeat"
              bgSize="cover"
            />
          </Col>
        </Row>
      </Flex>
      <Flex display={!isMobile ? "none" : ""} flexDirection="column">
        <DashboardArrecadacoes />
      </Flex>

      <Box mt="32px">
        <BoletosEmRegistro numeroBoletos={numeroBoletosEmRegistro} />
        <ArrecadacoesTable
          searchValue={filtro.search!}
          onSearch={(q) => onChangeFiltro({ search: q })}
          ano={filtro.ano!}
          mes={filtro.mes!}
          data={items}
          isLoading={_isLoading}
          selectedTipoBoleto={boletoExtra}
          onSelectTipoBoleto={setBoletoExtra}
          refetch={() => {
            refetch();
            setNumeroBoletosEmRegistro(undefined);
          }}
          onDeleted={() => refetch()}
          onEdit={(arrecadacao) => {
            setIsOpenModal(true);
            setArrecadacaoEditId(arrecadacao.id);
          }}
          onHistorico={setIdHistoricoArrecadacao}
          onOpenRateioModal={setRateio}
          onCloseRateio={(rateioId) =>
            showConfirm(
              "Deseja fechar esse rateio? Não será mais possível adicionar ou remover contas"
            ).then(({ isConfirmed }) => {
              isConfirmed && fecharRateio(rateioId);
            })
          }
          selectedItems={selectedItems}
          setSelectedsItems={setSelectedItems}
        />
      </Box>

      {isOpenModal ? (
        <ModalFormArrecadacao
          isOpen={isOpenModal}
          onClose={() => {
            setIsOpenModal(false);
            setArrecadacaoEditId(undefined);
            refetch();
          }}
          idArrecadacao={arrecadacaoEditId}
          year={moment(selectedDate).year().toString()}
          month={(moment(selectedDate).month() + 1).toString()}
        />
      ) : null}

      {!!rateio ? (
        <ModalFormRateio
          isOpenModal={!!rateio}
          month={(moment(selectedDate).month() + 1).toString()}
          year={moment(selectedDate).year().toString()}
          onClose={() => setRateio(undefined)}
          rateioId={rateio?.id}
          nomeRateio={rateio?.nome}
          onSuccess={() => {
            refetch();
            setRateio(undefined);
          }}
        />
      ) : null}

      {isOpenModalNotifyIsOpen ? (
        <ModalNotificacao
          isOpen={isOpenModalNotifyIsOpen}
          onClose={() => setIsOpenModalNotifyIsOpen(false)}
        />
      ) : null}

      {isOpenModalParametros ? (
        <ModalParametrosCobranca
          onClose={() => setIsOpenModalParametros(false)}
          isOpen={isOpenModalParametros}
        />
      ) : null}

      {isOpenModalParametrosArrecadacaoAutomatica ? (
        <ModalParametrosArrecadacaoAutomatica
          onClose={() => setIsOpenModalParametrosArrecadacaoAutomatica(false)}
          isOpen={isOpenModalParametrosArrecadacaoAutomatica}
        />
      ) : null}

      <ModalHistoricoArrecadacao
        onClose={() => setIdHistoricoArrecadacao(undefined)}
        id={idHistoricoArrecadacao}
        isOpenModal={!!idHistoricoArrecadacao}
      />
    </>
  );
}

export default ListaArrecadacoes;
